import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { CurrencyInput } from "@/components/ui/currency-input";
import { z } from "zod";

const valuationFormSchema = z.object({
  securityId: z.string().uuid(),
  valuationDate: z.string().min(1, "Valuation date is required"),
  valuationAmount: z.string().min(1, "Valuation amount is required"),
  valuationSource: z.enum([
    "avm_corelogic",
    "avm_proptrack",
    "avm_valocity",
    "full_valuation",
    "desktop_valuation",
    "kerbside_valuation",
    "contract_price",
    "bank_valuation",
    "self_assessed",
    "redbook",
    "glass_guide",
    "manual_entry",
  ]),
  valuerId: z.string().uuid().optional().nullable(),
  notes: z.string().optional().nullable(),
  isPrimary: z.boolean().default(false),
});

type ValuationFormData = z.infer<typeof valuationFormSchema>;

interface ValuationFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  securityId: string;
  valuation?: any;
  onSuccess?: () => void;
}

const VALUATION_SOURCE_OPTIONS = [
  { value: "avm_corelogic", label: "AVM CoreLogic" },
  { value: "avm_proptrack", label: "AVM PropTrack" },
  { value: "avm_valocity", label: "AVM Valocity" },
  { value: "full_valuation", label: "Full Valuation" },
  { value: "desktop_valuation", label: "Desktop Valuation" },
  { value: "kerbside_valuation", label: "Kerbside Valuation" },
  { value: "contract_price", label: "Contract Price" },
  { value: "bank_valuation", label: "Bank Valuation" },
  { value: "self_assessed", label: "Self Assessed" },
  { value: "redbook", label: "RedBook" },
  { value: "glass_guide", label: "Glass Guide" },
  { value: "manual_entry", label: "Manual Entry" },
];

export function ValuationFormModal({
  open,
  onOpenChange,
  securityId,
  valuation,
  onSuccess,
}: ValuationFormModalProps) {
  const { toast } = useToast();
  const isEditing = !!valuation;

  const form = useForm<ValuationFormData>({
    resolver: zodResolver(valuationFormSchema),
    defaultValues: {
      securityId,
      valuationDate: valuation?.valuationDate || new Date().toISOString().split('T')[0],
      valuationAmount: valuation?.valuationAmount || "",
      valuationSource: valuation?.valuationSource || "manual_entry",
      valuerId: valuation?.valuerId || null,
      notes: valuation?.notes || "",
      isPrimary: valuation?.isPrimary || false,
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: ValuationFormData) => {
      const endpoint = isEditing
        ? `/api/crm/securities/${securityId}/valuations/${valuation.id}`
        : `/api/crm/securities/${securityId}/valuations`;
      const method = isEditing ? "PATCH" : "POST";

      const { securityId: _securityId, ...updates } = data;
      const response = await apiRequest(method, endpoint, isEditing ? updates : data);
      if (!response.ok) {
        const error = await response.text();
        throw new Error(error || "Failed to save valuation");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/crm/securities/${securityId}/valuations`] });
      queryClient.invalidateQueries({ queryKey: [`/api/crm/securities/${securityId}`] });
      toast({
        title: "Success",
        description: `Valuation ${isEditing ? "updated" : "created"} successfully`,
      });
      onOpenChange(false);
      form.reset();
      onSuccess?.();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: ValuationFormData) => {
    createMutation.mutate(data);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit" : "Add"} Valuation</DialogTitle>
          <DialogDescription>
            {isEditing ? "Update" : "Record"} a valuation for this security
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="valuationDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Valuation Date</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="valuationAmount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Amount</FormLabel>
                    <FormControl>
                      <CurrencyInput
                        value={field.value}
                        onValueChange={field.onChange}
                        placeholder="1,000,000.00"
                        data-testid="valuation-amount-input"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="valuationSource"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Valuation Source</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select source" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {VALUATION_SOURCE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="isPrimary"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      data-testid="is-primary-checkbox"
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Primary Valuation</FormLabel>
                    <FormDescription>
                      Mark this as the authoritative valuation for this security.
                      Only one valuation can be primary per security.
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes (Optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Additional valuation details..."
                      className="min-h-[80px]"
                      {...field}
                      value={field.value || ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={createMutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {isEditing ? "Update" : "Add"} Valuation
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
